/**
 * Ligar e desligar o aviso PUSH neste aparelho (o servidor é `src/push.ts`).
 *
 * A inscrição é do NAVEGADOR: ligar no computador não liga no celular, e é o
 * que se quer — cada um escolhe onde quer ser interrompido.
 */
import * as api from "./api.js";

export const suportado = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const registro = async () => (await navigator.serviceWorker.getRegistration()) ?? navigator.serviceWorker.register("/sw.js");

/** `ligado`, `desligado`, `bloqueado` (o navegador recusou) ou `sem` (não há push aqui). */
export async function situacao() {
  if (!suportado()) return "sem";
  if (Notification.permission === "denied") return "bloqueado";
  const r = await navigator.serviceWorker.getRegistration();
  return (await r?.pushManager.getSubscription()) ? "ligado" : "desligado";
}

const daChave = (b64) => {
  const bruto = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(bruto, (c) => c.charCodeAt(0));
};

/* Quem tocou em "Parar de avisar" escolheu: a mesa não religa sozinha. */
const MARCA = "push-desligado";
const lembrar = (valor) => {
  try {
    if (valor) localStorage.setItem(MARCA, "1");
    else localStorage.removeItem(MARCA);
  } catch {
    /* sem armazenamento: religa na próxima entrada, que é o lado seguro */
  }
};
const desligadoAMao = () => {
  try {
    return localStorage.getItem(MARCA) === "1";
  } catch {
    return false;
  }
};

/**
 * Liga sozinho ao entrar na mesa.
 *
 * Permissão já dada: inscreve de novo em silêncio (reaparelha o login, que pode
 * ser outro neste navegador, e refaz a inscrição que o navegador expirou).
 * Nunca perguntada: a pergunta do navegador sai no PRIMEIRO toque na mesa — o
 * navegador só a mostra depois de um gesto, e é o mais cedo possível.
 * Bloqueada ou desligada no sino: nada.
 */
let armado = false;
export function automatico() {
  if (!suportado() || desligadoAMao()) return;
  if (Notification.permission === "granted") return void ligar().catch(() => {});
  if (Notification.permission !== "default" || armado) return;
  armado = true;
  const pedir = () => {
    removeEventListener("pointerdown", pedir, true);
    removeEventListener("keydown", pedir, true);
    ligar().catch(() => {});
  };
  addEventListener("pointerdown", pedir, true);
  addEventListener("keydown", pedir, true);
}

export async function ligar() {
  /* Primeira linha, sem `await` antes: o Safari só aceita o pedido dentro do
     gesto, e qualquer espera antes dele o tira do gesto. */
  if ((await Notification.requestPermission()) !== "granted") return situacao();
  lembrar(false);
  const { chave } = await api.pegar("/api/push/chave");
  const r = await registro();
  await navigator.serviceWorker.ready;
  const inscricao =
    (await r.pushManager.getSubscription()) ??
    (await r.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: daChave(chave) }));
  await api.mandar("/api/push/inscricao", inscricao.toJSON());
  return "ligado";
}

/** `aMao`: a pessoa escolheu no sino. Sair do sistema desliga sem marcar. */
export async function desligar(aMao = true) {
  if (aMao) lembrar(true);
  const r = await navigator.serviceWorker.getRegistration();
  const inscricao = await r?.pushManager.getSubscription();
  if (inscricao) {
    await api.mandar("/api/push/inscricao", { endpoint: inscricao.endpoint }, "DELETE").catch(() => {});
    await inscricao.unsubscribe();
  }
  return "desligado";
}

/** iPhone no Safari comum: lá não há push — só com a mesa na Tela de Início. */
export const precisaInstalar = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) && !navigator.standalone && !matchMedia("(display-mode: standalone)").matches;
